/**
 * scRnaExperimentHelpers.js
 *
 * scRNA-seq 實驗類型（SC 代碼）的純函式。集中維護兩件事：
 *
 * 1. 每個分析服務品項支援哪些 SC 實驗類型 —— 不支援的仍要顯示在下拉選單中，
 *    但不可選取，讓客戶知道該實驗類型存在、只是這個 pipeline 不支援。
 * 2. 每個 SC 實驗類型對 Sample Sheet 的額外欄位與填寫提示。
 */

const CELL_RANGER = { name: 'CellRanger', version: 'v10.0.1' };
const CELE_SCOPE = { name: 'CeleScope', version: 'v2.9.0' };
const CELL_BENDER = { name: 'CellBender', version: 'v0.3.2' };

/**
 * 分析服務品項的規則，兩個欄位各自獨立、都可省略：
 *
 * - disabledExperimentCodes：該 pipeline 不支援的實驗類型。實驗類型選單一律先依品項的平台
 *   過濾（10X 品項只會出現 SC01-SC08，Singleron 品項只會出現 SC09），這裡列到的再標記為
 *   disabled（仍然顯示，只是不能選）。
 * - tools：「分析流程」摘要要顯示的工具版本。
 *
 * 沒有列在這裡的品項就只做平台過濾，且不顯示工具版本。
 */
const ANALYSIS_SERVICE_RULES = {
  // 10X Genomics 平台
  'A-B012': {
    disabledExperimentCodes: ['SC03', 'SC07', 'SC08'],
    tools: [CELL_RANGER]
  },
  'A-B013': {
    disabledExperimentCodes: ['SC03', 'SC04', 'SC07', 'SC08'],
    tools: [CELL_RANGER, CELL_BENDER]
  },
  // 客製化品項 = Basic / Standard 的 pipeline 能力再加上客製化處理，
  // 所以工具組與不支援的實驗類型都比照對應的品項。
  'A-B014': {
    disabledExperimentCodes: ['SC03', 'SC07', 'SC08'],
    tools: [CELL_RANGER]
  },
  'A-B015': {
    disabledExperimentCodes: ['SC03', 'SC04', 'SC07', 'SC08'],
    tools: [CELL_RANGER, CELL_BENDER]
  },
  // Singleron 平台（只有 SC09，沒有不支援的實驗類型）
  'A-B017': { tools: [CELE_SCOPE] },
  'A-B018': { tools: [CELE_SCOPE, CELL_BENDER] },
  'A-B019': { tools: [CELE_SCOPE] },
  'A-B020': { tools: [CELE_SCOPE, CELL_BENDER] }
};

/**
 * SC 實驗類型對 Sample Sheet 的額外要求。未列出者代表只需填 Sample_Name。
 *
 * unlockRowCount：一般分析服務的 Sample Sheet 列數會被步驟三的數量鎖定，但有些實驗類型
 * 使用者在步驟三填的其實是 fastq / library 數，和樣本數對不起來，而且兩個方向都會發生：
 *
 * - SC02 / SC06：一份 library / fastq 會 demultiplex 成多個樣本 → 列數多於步驟三數量
 * - SC04：一個樣本會有多份 fastq（GEX + BCR/TCR）→ 列數少於步驟三數量
 *
 * 因為兩個方向都可能，這裡不設上下限，列數完全交給使用者決定（至少一列）。
 */
const EXPERIMENT_SAMPLE_SHEET_RULES = {
  SC02: {
    requireSampleBarcode: true,
    showFastqName: false,
    unlockRowCount: true,
    hints: [
      'CellPlex（CMO）為 multiplex 實驗，請為每個樣本填寫對應的 Sample_Barcode，分析時才能正確 demultiplexing。',
      '一個 CellPlex library 會 demultiplex 成多個樣本，本表請一個樣本一列。列數由你自行增減，通常會多於步驟三填寫的數量。'
    ]
  },
  SC04: {
    requireSampleBarcode: false,
    showFastqName: true,
    requireFastqName: true,
    fastqNameLabel: 'GEX_Fastq_Name',
    fastqNamePlaceholder: 'apple_GEX',
    fastqNameHint: 'GEX library 的 fastq 檔名前綴，例如 apple_GEX_R1_001.fastq.gz 只需填 apple_GEX',
    requireVdjLibrary: true,
    unlockRowCount: true,
    hints: [
      "請在每一列選擇這個樣本搭配的 V(D)J enrichment：BCR、TCR、兩種都做，或「無（只做 5' GEX）」。",
      '5\' GEX 絕大多數會搭配一種 V(D)J enrichment，只做 GEX 的情況請明確選「無」，不要留空。',
      '一組樣本只需填寫一個 Sample_Name，並分別填入各 library 的 fastq 檔名前綴。例如 apple_GEX_R1_001.fastq.gz 的 GEX_Fastq_Name 填 apple_GEX，apple_BCR_R1_001.fastq.gz 的 BCR_Fastq_Name 填 apple_BCR。',
      'fastq 檔名不必含有 BCR / TCR 字樣，分析時以你在本表填的對應關係為準。',
      '因此本表一個樣本只要一列，不是一份 fastq 一列。列數由你自行增減，通常會少於步驟三填寫的數量。'
    ]
  },
  SC06: {
    requireSampleBarcode: true,
    showFastqName: true,
    unlockRowCount: true,
    hints: [
      'Flex multiplex library 需提供 Sample_Barcode 以進行 demultiplexing。',
      '請為每一組 barcode 填寫其來源的 Fastq_Name（fastq 檔名前綴，例如 test1_S1_L001_R1_001.fastq.gz 只需填 test1）與該樣本最終要使用的 Sample_Name。',
      '一份 fastq 會 demultiplex 成多個樣本，本表請一個樣本一列。列數由你自行增減，通常會多於步驟三填寫的數量。'
    ]
  }
};

/** 選到特定 SC 實驗類型時要額外顯示的提醒。 */
const EXPERIMENT_NOTICES = {
  SC09: `${CELE_SCOPE.name} 版本為 ${CELE_SCOPE.version}；此實驗類型的物種僅支援 Human 與 Mouse。`
};

const EMPTY_SAMPLE_SHEET_RULE = {
  requireSampleBarcode: false,
  showFastqName: false,
  // showFastqName 只決定欄位要不要出現，requireFastqName 才決定它是不是必填
  // （SC06 的 Fastq_Name 是選填，SC04 的 GEX_Fastq_Name 是必填）。
  requireFastqName: false,
  fastqNameLabel: 'Fastq_Name',
  fastqNamePlaceholder: 'test1',
  fastqNameHint: 'fastq 檔名前綴，例如 test1_S1_L001_R1_001.fastq.gz 只需填 test1',
  requireVdjLibrary: false,
  unlockRowCount: false,
  hints: []
};

/**
 * SC04（5' GEX + Immune Profiling）的 V(D)J library 組合。
 *
 * 一個 5' GEX 一定會搭配 V(D)J enrichment，而後端是靠 fastq 名稱把 GEX 與 BCR/TCR 配成一組。
 * 客戶不見得會把 fastq 命名成 *_BCR，所以配對關係由使用者在 Sample Sheet 明確填寫，
 * 這樣即使 fastq 名稱看不出 library 種類，也能從訂單資料回溯是哪個 fastq 配哪個。
 */
export const VDJ_LIBRARY_TYPE_OPTIONS = [
  { value: 'BCR', label: 'BCR' },
  { value: 'TCR', label: 'TCR' },
  { value: 'BCR+TCR', label: 'BCR + TCR' },
  // 少數樣本只做 5' GEX。這個選項要明確選，才分得出「真的沒做」與「忘了填」。
  { value: 'NONE', label: "無（只做 5' GEX）" }
];

/** 每個 V(D)J library 對應的 Sample Sheet 欄位。 */
export const VDJ_LIBRARY_FASTQ_FIELDS = [
  { library: 'BCR', field: 'bcrFastqName', label: 'BCR_Fastq_Name' },
  { library: 'TCR', field: 'tcrFastqName', label: 'TCR_Fastq_Name' }
];

/** 貼上或匯入的值可能寫成 bcr、BCR + TCR、TCR/BCR、無 等，一律正規化成選單裡的值。 */
export const normalizeVdjLibraryType = (value = '') => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';

  const letters = raw.toUpperCase().replace(/[^A-Z]/g, '');
  if (letters === 'BCRTCR' || letters === 'TCRBCR') return 'BCR+TCR';
  if (letters === 'BCR' || letters === 'TCR') return letters;
  if (raw.startsWith('無') || letters === 'NONE' || letters === 'GEX' || letters === 'GEXONLY') return 'NONE';

  return '';
};

/** 畫面上顯示用的文字（資料裡存的是 BCR+TCR / NONE 這種值）。 */
export const formatVdjLibraryTypeLabel = (value = '') => {
  const normalized = normalizeVdjLibraryType(value);
  return VDJ_LIBRARY_TYPE_OPTIONS.find(option => option.value === normalized)?.label || '';
};

export const vdjLibraryTypeIncludes = (value = '', library = '') => (
  normalizeVdjLibraryType(value).split('+').includes(String(library || '').toUpperCase())
);

export const extractScRnaExperimentCode = (experimentServiceName = '') => {
  const match = String(experimentServiceName || '').trim().match(/^(SC\d+)/i);
  return match ? match[1].toUpperCase() : '';
};

export const extractAnalysisServiceCode = (analysisServiceValue = '') => {
  const match = String(analysisServiceValue || '').trim().match(/^([A-Za-z]-[A-Za-z]\d+)/);
  return match ? match[1].toUpperCase() : '';
};

export const getScRnaAnalysisServiceRule = (analysisServiceValue = '') => {
  const rule = ANALYSIS_SERVICE_RULES[extractAnalysisServiceCode(analysisServiceValue)];
  if (!rule) return null;

  return {
    disabledExperimentCodes: rule.disabledExperimentCodes || [],
    tools: rule.tools || []
  };
};

export const getScRnaExperimentSampleSheetRule = (experimentServiceName = '') => ({
  ...EMPTY_SAMPLE_SHEET_RULE,
  ...(EXPERIMENT_SAMPLE_SHEET_RULES[extractScRnaExperimentCode(experimentServiceName)] || {})
});

/**
 * Sample Sheet 一列的欄位驗證（Sample_Name 由呼叫端自己驗，因為每種單的規則不同）。
 * 回傳 { 欄位: 錯誤訊息 }，沒有錯誤時為空物件。
 */
export const getScRnaSampleSheetRowErrors = ({
  row = {},
  rule = EMPTY_SAMPLE_SHEET_RULE
} = {}) => {
  const errors = {};
  const text = value => String(value ?? '').trim();

  if (rule.requireSampleBarcode && !text(row.sampleBarcode)) {
    errors.sampleBarcode = 'Sample_Barcode 不可為空';
  }

  if (rule.requireFastqName && !text(row.fastqName)) {
    errors.fastqName = `${rule.fastqNameLabel} 不可為空`;
  }

  if (rule.requireVdjLibrary) {
    const vdjType = normalizeVdjLibraryType(row.vdjType);

    if (!vdjType) {
      errors.vdjType = '請選擇 V(D)J 類型';
    } else {
      VDJ_LIBRARY_FASTQ_FIELDS.forEach(({ library, field, label }) => {
        if (vdjLibraryTypeIncludes(vdjType, library) && !text(row[field])) {
          errors[field] = `${label} 不可為空`;
        }
      });
    }
  }

  return errors;
};

/**
 * 清掉這個實驗類型用不到的欄位，避免換實驗類型 / 換品項後舊值跟著送出、寫進資料庫。
 * 其餘欄位原封不動。
 */
export const sanitizeScRnaSampleSheetRow = (row = {}, rule = EMPTY_SAMPLE_SHEET_RULE) => {
  const vdjType = rule.requireVdjLibrary ? normalizeVdjLibraryType(row.vdjType) : '';

  return {
    ...row,
    sampleBarcode: rule.requireSampleBarcode ? (row.sampleBarcode ?? '') : '',
    fastqName: rule.showFastqName ? (row.fastqName ?? '') : '',
    vdjType,
    ...Object.fromEntries(VDJ_LIBRARY_FASTQ_FIELDS.map(({ library, field }) => [
      field,
      vdjLibraryTypeIncludes(vdjType, library) ? (row[field] ?? '') : ''
    ]))
  };
};

export const getScRnaExperimentNotice = (experimentServiceName = '') => (
  EXPERIMENT_NOTICES[extractScRnaExperimentCode(experimentServiceName)] || ''
);

/** 分析流程摘要要顯示的工具版本。沒有設定規則的品項不顯示版本。 */
export const getScRnaPipelineToolVersions = ({ analysisServiceValue = '' } = {}) => (
  getScRnaAnalysisServiceRule(analysisServiceValue)?.tools ?? []
);

export const formatScRnaToolVersionText = (tools = []) => (
  (Array.isArray(tools) ? tools : [])
    .map(tool => `${tool.name} 版本為 ${tool.version}。`)
    .join('')
);

/** experiment_info.config.json 的 platform_type 對應到送單/分析需求單使用的平台名稱。 */
const PLATFORM_LABELS_BY_CONFIG_KEY = {
  '10xgenomics': '10X Genomics',
  singleron: 'Singleron'
};

/**
 * 送出用的 platform_type：以「實驗類型」的平台為準，而不是分析服務品項的平台。
 * 例如 A-B013（scRNAseq-10X_Standard）搭配 SC09 時，資料實際來自 Singleron，
 * 分析需求單也要勾在 Singleron 平台的區塊。找不到對應時退回分析服務品項的平台。
 */
export const resolveScRnaPlatformLabel = (experimentOption = null, fallbackLabel = '') => {
  const configKey = String(experimentOption?.platform_type || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

  return PLATFORM_LABELS_BY_CONFIG_KEY[configKey] || fallbackLabel || '';
};

const getPlatformKey = (platformLabel = '') => {
  const normalized = String(platformLabel || '').toLowerCase();
  if (normalized.includes('10x')) return '10xgenomics';
  if (normalized.includes('singleron')) return 'singleron';
  return '';
};

const flattenExperimentCatalog = (experimentInfo = {}) => (
  Object.values(experimentInfo?.scRNAseq || {})
    .flatMap(platformOptions => (Array.isArray(platformOptions) ? platformOptions : []))
);

export const getScRnaExperimentOptionsForPlatform = (experimentInfo = {}, platformLabel = '') => {
  const platformOptions = experimentInfo?.scRNAseq?.[getPlatformKey(platformLabel)];
  return Array.isArray(platformOptions) ? platformOptions : [];
};

/** 查詢用：不受平台限制，直接在整份設定檔中找出該實驗類型。 */
export const getScRnaExperimentCatalog = (experimentInfo = {}) => flattenExperimentCatalog(experimentInfo);

/**
 * 下拉選單用：先依品項的平台取實驗類型，再把該 pipeline 不支援的標記為 disabled
 * （仍然顯示，只是不能選）。
 */
export const buildScRnaExperimentOptions = ({
  experimentInfo = {},
  platformLabel = '',
  analysisServiceValue = ''
} = {}) => {
  const rule = getScRnaAnalysisServiceRule(analysisServiceValue);

  return getScRnaExperimentOptionsForPlatform(experimentInfo, platformLabel).map(option => ({
    ...option,
    disabled: Boolean(rule)
      && rule.disabledExperimentCodes.includes(extractScRnaExperimentCode(option?.ServiceName))
  }));
};
